import { useState } from "react";
import { Expand, Monitor, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReviewImage from "./ReviewImage";

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

function Chip({ warn, children, testid }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${warn ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600"}`} data-testid={testid}>{children}</span>;
}

function Metrics({ m, vp, testid }) {
  if (!m) return null;
  const logoSmall = m.logo && m.logo.h < (vp === "desktop" ? 40 : 32);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      <Chip warn={!m.logo || logoSmall}>Logo {m.logo ? `${m.logo.w}×${m.logo.h}px` : "non trovato"}</Chip>
      <Chip warn={(m.first_screen_image_share || 0) < 0.2}>Immagini nel primo schermo {pct(m.first_screen_image_share)}</Chip>
      <Chip warn={m.h1_count !== 1}>H1: {m.h1_count}</Chip>
      {m.overflow_px > 0 && <Chip warn>Sborda di {m.overflow_px}px</Chip>}
      {m.broken_images > 0 && <Chip warn>{m.broken_images} immagini rotte</Chip>}
    </div>
  );
}

function FullPage({ rid, page, vp, open, onOpenChange }) {
  const v = page.views[vp] || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto [&>*]:min-w-0" data-testid="review-fullpage-dialog">
        <DialogHeader>
          <DialogTitle>{page.name} · {vp === "desktop" ? "desktop 1440px" : "mobile 390px"}</DialogTitle>
          <DialogDescription>Le etichette a sinistra (S1, S2…, H header, F footer) sono quelle che vede l'AI: non fanno parte del sito.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">{(v.tiles || []).map((t) => <ReviewImage key={t} rid={rid} name={t} className="w-full rounded border" alt="" />)}</div>
        {v.truncated && <p className="text-xs text-amber-700">Pagina molto lunga: l'ultima parte non è inclusa negli screenshot.</p>}
      </DialogContent>
    </Dialog>
  );
}

function PageShots({ rid, page }) {
  const [full, setFull] = useState(null);
  const d = page.views.desktop || {};
  const m = page.views.mobile || {};
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`review-page-${page.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900">{page.name} <span className="font-normal text-slate-500">{page.path}</span></p>
        <p className="text-[11px] text-slate-500">{page.sections.filter((s) => s.kind === "section").length} sezioni</p>
      </div>
      <div className="mt-3 grid grid-cols-[1fr_auto] gap-3 items-start">
        <div className="min-w-0 space-y-2">
          <div className="relative group">
            {d.error ? <p className="text-xs text-red-600">{d.error}</p> : <ReviewImage rid={rid} name={d.first} className="w-full aspect-[1440/900] object-cover object-top rounded-lg border" testid={`review-first-desktop-${page.key}`} />}
            {!d.error && <Button size="sm" variant="secondary" className="absolute right-2 top-2 h-7 text-xs opacity-90" onClick={() => setFull("desktop")} data-testid={`review-full-desktop-${page.key}`}><Expand size={12} className="mr-1" />Pagina intera</Button>}
          </div>
          <p className="flex items-center gap-1 text-[11px] text-slate-500"><Monitor size={12} />Desktop</p>
          <Metrics m={d.metrics} vp="desktop" testid={`review-metrics-desktop-${page.key}`} />
        </div>
        <div className="w-[110px] sm:w-[150px] space-y-2">
          <div className="relative">
            {m.error ? <p className="text-xs text-red-600">{m.error}</p> : <ReviewImage rid={rid} name={m.first} className="w-full aspect-[390/844] object-cover object-top rounded-lg border" testid={`review-first-mobile-${page.key}`} />}
          </div>
          <button className="flex items-center gap-1 text-[11px] text-sky-700 hover:underline" onClick={() => setFull("mobile")} data-testid={`review-full-mobile-${page.key}`}><Smartphone size={12} />Mobile · intera</button>
          <Metrics m={m.metrics} vp="mobile" testid={`review-metrics-mobile-${page.key}`} />
        </div>
      </div>
      {full && <FullPage rid={rid} page={page} vp={full} open onOpenChange={(o) => !o && setFull(null)} />}
    </div>
  );
}

export default function RenderGallery({ rev }) {
  const pages = rev.render?.pages || [];
  return (
    <div className="space-y-3" data-testid="review-gallery">
      <p className="text-xs text-slate-500">{pages.length} pagine · compilazione {Math.round((rev.render?.build_ms || 0) / 100) / 10} s · screenshot in {rev.render?.seconds} s · costo 0 $</p>
      <div className="grid gap-3 xl:grid-cols-2">{pages.map((p) => <PageShots key={p.page_id} rid={rev.id} page={p} />)}</div>
    </div>
  );
}
